import onScan from 'onscan.js';
import { computed, ComputedRef, Ref, ref } from 'vue';

export interface BarcodeSubscriberCallback {
    (barcode: string): void
};

export class BarcodeListener {
    constructor(defaultSubscriber: BarcodeSubscriberCallback) {
        this.defaultSubscriber = defaultSubscriber;
    }

    isScanning: ComputedRef<boolean> = computed(() => this.processingKeyCount.value >= 6);
    scannedBarcode: Ref<string> = ref('');

    defaultSubscriber: BarcodeSubscriberCallback;
    subscriber: BarcodeSubscriberCallback | null = null;

    changeSubscriber = (subscriber: BarcodeSubscriberCallback): void => {
        this.subscriber = subscriber;
    };

    removeSubscriber = (): void => {
        this.subscriber = null;
    };

    handleBarcodeScan = (barcode: string): void => {
        this.scannedBarcode.value = barcode;
        setTimeout(() => {
            this.scannedBarcode.value = '';
        }, 2000);
        this.processingKeyCount.value = 0;

        if (this.handleCommandCodes(barcode)) {
            return;
        }

        if (this.handleActiveElements(barcode)) {
            return;
        }

        if (this.subscriber) {
            this.subscriber(barcode);
        } else {
            this.defaultSubscriber(barcode);
        }
    };

    handleActiveElements = (barcode: string): boolean => {
        const activeElement = document.activeElement;

        if (activeElement instanceof HTMLInputElement) {
            activeElement.value = barcode;
            activeElement.dispatchEvent(new Event('input'));

            return true;
        }

        if (activeElement instanceof HTMLTextAreaElement) {
            // Fix issues with typing really fast, mostly on Dusk tests
            if (activeElement.value.includes(barcode)) {
                return true;
            }

            activeElement.value += `\n${barcode}`;
            activeElement.dispatchEvent(new Event('input'));

            return true;
        }

        return false;
    };

    /**
     * Example command codes:
     * $$(Control+/) - Sends Ctrl + / at the same time
     * $$(ArrowDown)$$(Enter) - Sends the down arrow, then the enter key
     */
    handleCommandCodes = (barcode: string): boolean => {
        if (barcode.includes('$$(')) {
            const keys: string[] = [];

            barcode.split('$$').forEach((barcodeKey) => {
                if (!barcodeKey) {
                    return;
                }

                keys.push(barcodeKey
                    .replace('(', '')
                    .replace(')', ''),
                );
            });

            this.sendKeyCombination(keys);

            return true;
        }
        return false;
    };

    sendKeyCombination = (keyCombinations: string[]): void => {
        keyCombinations.forEach((keyCombination) => {
            let key = keyCombination;
            let ctrlKey = false;
            if (keyCombination.includes('+')) {
                const splitKey = key.split('+');
                if (splitKey[0] === 'Control') {
                    ctrlKey = true;
                }

                const lastKey = splitKey[splitKey.length - 1];
                if (lastKey != null && lastKey !== '') {
                    key = lastKey;
                }
            }

            document.dispatchEvent(new KeyboardEvent('keydown', { key, ctrlKey }));
            document.dispatchEvent(new KeyboardEvent('keyup', { key }));
        });
    };

    processingKeyCount: Ref<number> = ref(0);
    handleKeyProcess = (keyCode: string, _keyEvent: KeyboardEvent): void => {
        if (keyCode === '') return;
        this.processingKeyCount.value++;
    };

    handleScanError = (_debug: unknown): void => {
        this.processingKeyCount.value = 0;
    };

    mapKeyCode = (scanKeyEvent: KeyboardEvent): string | null => {
        if (scanKeyEvent.code === 'Minus') return scanKeyEvent.shiftKey ? '_' : '-';
        // Return the key if it's in the allowed characters list
        const allowedCharactersRegex = /[\-,'\s\w$/{}[\]:".]/;
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- key is typed as always-defined, but some scanner hardware/browsers dispatch keydown events with an undefined key at runtime (Sentry PREPBUSINESS-B7N)
        if (scanKeyEvent.key?.length === 1 && allowedCharactersRegex.test(scanKeyEvent.key)) {
            return scanKeyEvent.key;
        }

        return null;
    };

    initScanner = (avgTimeByCharMs?: number): void => {
        if (onScan.isAttachedTo(document)) {
            onScan.detachFrom(document);
        }

        onScan.attachTo(document, {
            onScan: scannedCode => this.handleBarcodeScan(scannedCode),
            suffixKeyCodes: [13],
            timeBeforeScanTest: 300,
            avgTimeByChar: avgTimeByCharMs ?? 50,
            keyCodeMapper: scanKeyEvent => this.mapKeyCode(scanKeyEvent),
            onScanError: _debugData => this.handleScanError(_debugData),
            onKeyProcess: (scanKeyCode: string, scanKeyEvent: KeyboardEvent) => this.handleKeyProcess(scanKeyCode, scanKeyEvent),
        });
    };
}
